import EmptyState from '@/components/linear/empty-state';
import PageToolbar from '@/components/linear/page-toolbar';
import { invoicePath, newInvoicePath } from '@/lib/linear-paths';
import {
  formatUsdFromCents,
  invoiceLabel,
  invoiceStatusLabel,
  type Customer,
  type Invoice,
} from '@/types/invoice';
import { Link } from '@inertiajs/react';

interface Props {
  invoices?: Invoice[];
  customers?: Customer[];
}

export default function Index({ invoices = [] }: Props) {
  return (
    <div className="ln-page">
      <PageToolbar
        title="Invoices"
        actions={
          <Link href={newInvoicePath()} className="ln-primary-btn" style={{ marginLeft: 0 }}>
            New invoice
          </Link>
        }
        showNew={false}
      />
      <div className="ln-page-scroll">
        {invoices.length === 0 ? (
          <EmptyState title="No invoices" description="Create an invoice to bill a customer." />
        ) : (
          invoices.map((invoice) => (
            <Link
              key={invoice.id}
              href={invoicePath(invoice.id)}
              className="ln-row"
              style={{ height: 52 }}
            >
              <span className="ln-row-id">{invoiceLabel(invoice)}</span>
              <span className="ln-row-title">{invoice.customer?.name || 'No customer'}</span>
              <span className="ln-label">{invoiceStatusLabel(invoice.status)}</span>
              <span className="ln-row-meta">
                <span className="ln-inbox-sub">{formatUsdFromCents(invoice.totalCents)}</span>
                <span className="ln-row-date">{formatSentOn(invoice.sentAt)}</span>
              </span>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

function formatSentOn(sentAt: string | null): string {
  if (!sentAt) return 'Not sent';

  const date = parseDate(sentAt);
  if (!date) return 'Not sent';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: date.timeZone,
  }).format(date.value);
}

function parseDate(value: string): { value: Date; timeZone?: 'UTC' } | null {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return {
      value: new Date(Date.UTC(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))),
      timeZone: 'UTC',
    };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  return { value: parsed };
}
